//!#system	

import router from '@/router';
import Handlebars from 'handlebars';
import helperMarkdown from 'helper-markdown';
import qs from 'qs';
import moment from 'moment';

Handlebars.registerHelper('markdown', helperMarkdown());
Handlebars.registerHelper('dateFormat', function(str, formatStr) {
	return moment(str).format(formatStr);
});
Handlebars.registerHelper('comma', function() {
	return ',';
});

const templateProcessors = {};

const TimeElapsed = function() {
	if (!(this instanceof TimeElapsed)) {
		return new TimeElapsed();
	}
	let startTime, endTime;

	this.start = () => {
		startTime = new Date();
		return this;
	};

	this.end = () => {
		endTime = new Date();
		let timeDiff = endTime - startTime; //in ms
		// strip the ms
		timeDiff /= 1000;
		// get seconds 
		const seconds = Math.round(timeDiff);
		return seconds;
	}
};

const ID_SUFFIX = '_id';

export default {

	elapsed() {
		return new TimeElapsed();
	},

	escapeHTML(str) {
		return str.replace(/[&<>'"]/g,
			tag => ({
				'&': '&amp;',
				'<': '&lt;',
				'>': '&gt;',
				"'": '&#39;',
				'"': '&quot;'
			} [tag] || tag))
	},

	isNullish(dat) {
		return dat === null || typeof dat === 'undefined';
	},

	isPojo(dat) {
		if (dat === null) return false; // nulls are objects
		if (dat instanceof Date) return false; // Dates are objects
		if (Array.isArray(dat)) return false; // Arrays are objects
		if (typeof dat === 'object') return true;
		return false;
	},

	isEmpty(dat) {
		if (this.isNullish(dat)) return true;
		if (Array.isArray(dat) && dat.length === 0) return true;
		if (typeof dat === 'object' && Object.keys(dat).length === 0) return true;
		if (typeof dat === 'object' && Object.keys(dat).length === 1 && Object.keys(dat)[0] === '__ob__') return true; // Observer object, that is empty
		return false;
	},

	isId(fieldName) {
		if (fieldName === 'id') return true;
		if (fieldName.endsWith(ID_SUFFIX)) return true;
		return false;
	},

	toIdField(fieldName) {
		if (this.isId(fieldName)) return fieldName;
		return fieldName + ID_SUFFIX;
	},

	randomString(len) {
		if (!len || typeof len !== 'number') len = 10;
		const rs = [...Array(len)].map(() => (~~(Math.random() * 36)).toString(36)).join('');
		return rs;
	},

	loadingText() {
		const phrases = ['Loading...', 'Getting the info...', 'Stand by, loading!', 'Just a sec...', 'Talking to the system...'];
		return phrases[Math.floor(Math.random() * phrases.length)];
	},

	compileTemplate(template) { // returns the template function
		try {
			return Handlebars.compile(template);
		} catch (err) {
			return () => {};
		}
	},

	template(type, template, data) {
		let stringResult;
		if (templateProcessors[type]) {
			stringResult = templateProcessors[type](data);
		} else {
			templateProcessors[type] = Handlebars.compile('{{#markdown}}' + template + '{{/markdown}}');
			stringResult = templateProcessors[type](data);
		}
		if (stringResult.includes('\\</p>')) {
			stringResult = stringResult.replace('\\</p>', '</p>');
		}

		stringResult = this.userMentionsFormat(stringResult);

		return stringResult;
	},

	disposableTemplate(template, data) {
		let stringResult;
		const func = Handlebars.compile('{{#markdown}}' + template + '{{/markdown}}');
		stringResult = func(data);
		if (stringResult.includes('\\</p>')) {
			stringResult = stringResult.replace('\\</p>', '</p>');
		}
		stringResult = this.userMentionsFormat(stringResult);
		return stringResult;
	},

	// All user mentions start with @
	userMentionsGetUsers(content) {
		let contentFormatted = content;

		if (content.startsWith('<p>')) {
			contentFormatted = contentFormatted.replace('<p>', '');
			contentFormatted = contentFormatted.replace('</p>', '');
		}

		const matches = contentFormatted.match(/(\s|^)@\w+/g);

		if (!matches) {
			return [];
		}

		return matches;
	},

	isQueued(object, queueCode) {
		if (!object || !object.queues) return false;
		return !!object.queues.find(q => q.queueCode === queueCode);
	},

	isBlocked(object) {
		if (!object || !object.queues) return false;
		return !!object.queues.find(q => q.blockingQueue === true);
	},

	queueColor(queue) {
		if (queue.color) return queue.color;
		if (queue.blockingQueue) return 'red';
		if (queue.highPriority) return 'orange';
		return 'blue';
	},
	
	queueIcon(queue) {
		if (queue.icon) return queue.icon;
		if (queue.blockingQueue) return 'mdi-cancel';
		if (queue.highPriority) return 'mdi-alert';
		return 'mdi-checkbox-marked-circle';
	},

	removeArrayElementByMatch(arr, match) {
		const i = arr.indexOf(match);
		if (i > -1) arr.splice(i, 1);
	},

	userMentionsFormat(content) {
		const users = this.userMentionsGetUsers(content);

		let formatted = content,
			username,
			href;

		users.map(userMentionString => {
			username = userMentionString.split('@')[1];

			if (username) {
				formatted = formatted.replace(
					'@' + username,
					'<a class="user-mention" href="javascript:void(0);">' + username + '</a>'
				);
			}
		});

		return formatted;
	},

	filtersFormat(possibleFilters) {
		if (!possibleFilters.filters) return;
		possibleFilters.filters.forEach(filter => {
			if (!filter.friendlyName) filter.friendlyName = this.camelToTitle(filter.name);
			if (filter.parent) filter.friendlyName = this.camelToTitle(filter.parent) + '/' + filter.friendlyName;
			filter.key = (filter.parent ? filter.parent + '-' : '') + filter.name;
		});
	},

	operatorsMap() {
		return {
			'=': ['mdi-equal', 'Equal to'],
			'>': ['mdi-greater-than', 'Greater than'],
			'>=': ['mdi-greater-than-or-equal', 'Greater than or equal to'],
			'<': ['mdi-less-than', 'Less than'],
			'<=': ['mdi-less-than-or-equal', 'Less than or equal to'],
			'in': ['mdi-arrow-right-bold-circle-outline', 'Any of'],
			'bw': ['mdi-chevron-double-right', 'Begins with'],
			'ew': ['mdi-chevron-double-left', 'Ends with'],
			'cn': ['mdi-swap-horizontal', 'Contains'],
			'rg': ['mdi-swap-horizontal-bold', 'In the range of'],
			'!=': ['mdi-not-equal', 'Not equal to'],
			'nbw': ['mdi-chevron-double-right', 'Does not begin with'],
			'new': ['mdi-chevron-double-left', 'Does not end with'],
			'ncn': ['mdi-swap-horizontal', 'Does not contain'],
			'regex': ['mdi-code-tags', 'Match regular expression'],
			'nrg': ['mdi-arrow-expand', 'Not in range']
		};
	},

	filtersOperatorsIconsMap() {
		const m = this.operatorsMap();
		if (arguments.length === 0) {
			return m;
		}
		if (arguments.length > 0 && arguments[0] === 'array') {
			return Object.keys(m);
		}
		return m[arguments[0]];
	},

	camelToTitle(camelCase) {
		let str = camelCase
			.replace(/([A-Z0-9])/g, (match) => ` ${match}`)
			.replace(/^./, (match) => match.toUpperCase())
			.replace(/([0-9A-Z]) /g, (match, x) => `${x}`) // makes S K U = SKU
			.trim();
		return str.replace(/([A-Z]) /g, '$1');
	},

	camelCaseToDash(str) {
		return str.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase();
	},

	dashToCamelCase(str) {
		return str.replace(/-([a-z])/g, g => g[1].toUpperCase());
	},

	dashToTitle(str) {
		return this.dashToCamelCase(this.camelToTitle(str));
	},

	underscoreToCamelCase(str) {
		return str.replace(/_([a-z])/g, g => g[1].toUpperCase());
	},

	camelToPascal(camelCase) {
		return camelCase
			.replace(/([A-Z])/g, (match) => `${match}`)
			.replace(/^./, (match) => match.toUpperCase())
			.trim();
	},

	titleCase(str) {
		return str.toLowerCase().split(' ').map(function(word) {
			return (word.charAt(0).toUpperCase() + word.slice(1));
		}).join(' ');
	},

	truncateTo(str, len) {
		if (typeof str !== 'string') return '';
		if (str.length > len) return str.substring(0, len) + '…';
		return str;
	},

	firstKey(obj) {
		for (let key in obj) {
			if (key === 'id') continue;
			if (key === 'createdAt') continue;
			if (key === 'updatedAt') continue;
			if (key === 'tags') continue;
			if (key === 'queues') continue;
			if (key.startsWith('_')) continue;
			return key;
		}
		return '';
	},

	firstKeyValue(obj) {
		const key = this.firstKey(obj);
		if (!key) return '';
		return obj[key];
	},

	routeParser(routeObj, routeInfo) {
		if ('id' in routeInfo.params) {
			routeObj.id = routeInfo.params.id;
		}
		return routeObj
	},

	toBucket(routeObj) {
		return routeObj.namespace + '/' + routeObj.pluralName;
	},

	ordinalSuffix(i) {
		const j = i % 10,
			k = i % 100;
		if (j == 1 && k != 11) {
			return i + "st";
		}
		if (j == 2 && k != 12) {
			return i + "nd";
		}
		if (j == 3 && k != 13) {
			return i + "rd";
		}
		return i + "th";
	},

	arrayEquals(a, b) {
		return Array.isArray(a) &&
			Array.isArray(b) &&
			a.length === b.length &&
			a.every((val, index) => val === b[index]);
	},

	prefKey(routeInfo, key) {
		return this.toBucket(routeInfo) + '/' + key;
	},

	fromBucket(str) {
		const [namespace, pluralName] = str.split('/');
		return { namespace, pluralName };
	},

	dirtyCopy(obj) {
		return JSON.parse(JSON.stringify(obj));
	},

	currentPage(withQueryString) {
		let wCurrent = window.location.pathname;

		if (withQueryString) {
			wCurrent += window.location.search;
		}

		if (wCurrent.indexOf('/ui/') === 0) {
			const ws = wCurrent.split('/ui');
			return ws[ws.length - 1];
		}
		return wCurrent;
	},

	isObject(inp) {
		if (inp === null) return false;
		if (typeof inp === 'undefined') return false;
		return typeof inp === 'object';
	},

	stringType(str) {
		if (str === null) return 'empty';
		if (typeof str !== 'string') return 'object';
		let reresult;
		if (!str) return 'empty';
		// eslint-disable-next-line
		reresult = str.match(/^\d{4}\-\d{2}\-\d{2}T\d{2}\:\d{2}\:\d{2}\.\d+Z$/) || [];
		if (reresult.length === 1) return 'date';
		reresult = str.match(/^\d{19}$/) || [];
		if (reresult.length === 1) return 'id';
		return 'string'; // generic
	},

	makeUrlParamsString(params) {
		const paramArray = [];
		if (this.isPojo(params)) {
			for (const param in params) {
				paramArray.push(`${encodeURIComponent(param)}=${encodeURIComponent(params[param])}`);
			}
		}
		if (paramArray.length) return '?' + paramArray.join('&');
		return '';
	},

	goToDetail(routeInfo, id, options) {
		if (typeof routeInfo === 'string' && routeInfo.includes('/')) routeInfo = this.fromBucket(routeInfo);
		if (!options) options = {};
		let basePath = '';
		if (options.basePath) basePath = options.basePath;
		if (routeInfo.basePath) basePath = routeInfo.basePath;
		if (routeInfo.id && !id) id = routeInfo.id;
		router.push('/app/' + basePath + encodeURIComponent(this.camelCaseToDash(routeInfo.namespace)) + '/' + encodeURIComponent(this.camelCaseToDash(routeInfo.pluralName)) + '/detail/' + encodeURIComponent(id) + this.makeUrlParamsString(options.params));
	},

	linkToDetail(routeInfo, id, options) {
		if (typeof routeInfo === 'string' && routeInfo.includes('/')) routeInfo = this.fromBucket(routeInfo);
		if (!options) options = {};
		let basePath = '';
		if (options.basePath) basePath = options.basePath;
		if (routeInfo.basePath) basePath = routeInfo.basePath;
		if (routeInfo.id && !id) id = routeInfo.id;
		return '/app/' + basePath + encodeURIComponent(this.camelCaseToDash(routeInfo.namespace)) + '/' + encodeURIComponent(this.camelCaseToDash(routeInfo.pluralName)) + '/detail/' + encodeURIComponent(id) + this.makeUrlParamsString(options.params);
	},

	goToList(routeInfo, options) {
		if (!options) options = {};
		let basePath = '';
		let filterQs = '';
		if (options.basePath) basePath = options.basePath;
		if (routeInfo.basePath) basePath = routeInfo.basePath;
		if (options.filters) filterQs = qs.stringify(typeof options.filters !== 'object' ? {} : options.filters, { depth: 100 });
		if (options.refresh) filterQs = qs.stringify({ _refresh_: true }, { depth: 100 });
		const path = '/app/' + basePath + encodeURIComponent(this.camelCaseToDash(routeInfo.namespace)) + '/' + encodeURIComponent(this.camelCaseToDash(routeInfo.pluralName)) + (filterQs ? '?' + filterQs : '');
		if (options.returnPath) return path;
		router.push(path);
	},

	linkToList(routeInfo, options) {
		return this.goToList(routeInfo, { ...options, returnPath: true });
	},

	avsan(param) {
		// eslint-disable-next-line
		return AvsAn.query(param).article;
	},

	kFormatter(num) {
		if (!num) return 0;
		return Math.abs(num) > 999 ? Math.sign(num)*((Math.abs(num)/1000).toFixed(1)) + 'k' : Math.sign(num)*Math.abs(num)
	},

	isJustCreated(dateCreated) {
		const createdMoment = moment(dateCreated);
		const recentEnoughMoment = moment().subtract(10, 'second');
		const nowishMoment = moment(createdMoment).add(2, 'second');
		const nowMoment = moment();
		return createdMoment.isAfter(recentEnoughMoment) && nowMoment.isBefore(nowishMoment);
	},

	detailParser(obj, data, meta, routeObj) {
		obj.detail = data;
		obj.fields = meta.fields || [];
		obj.objectName = meta.name;
		obj.rootFields = [];
		obj.comment = meta.comment;
		obj.editWindow = false;
		const doNotDisplayFields = ['id'];
		const doNotDisplay = (name) => {
			if (doNotDisplayFields.includes(name)) return true;
			let reresult;
			reresult = name.match(/^\w+_id$/) || [];
			if (reresult.length === 1) return true;
			return false;
		};
		const atEndsArray = [];
		const atEndsFields = ['createdAt', 'updatedAt'];
		if (meta.relatedParent_id) {
			obj.parent.id = meta.relatedParent_id;
		}
		obj.fields.forEach(field => {
			if (field.relationship === null && !doNotDisplay(field.name)) {
				if (atEndsFields.includes(field.name)) {
					atEndsArray.push({
						name: field.name,
						display: this.camelToTitle(field.name),
						value: data[field.name],
						type: field.type.primitive
					});
				} else {
					obj.rootFields.push({
						name: field.name,
						display: this.camelToTitle(field.name),
						value: data[field.name],
						type: field.type.primitive
					});
				}
			}
		});
		obj.rootFields = obj.rootFields.concat(atEndsArray);
		if (routeObj) {
			obj.routeInfo = this.routeParser(routeObj)
			obj.listPath = obj.routeInfo.listPath;
		}
	},
	lightOrDark(color) {
		let r, g, b, hsp;

		const hexToRgba = function(hex) {
			const isValidHex = (hex) => /^#([A-Fa-f0-9]{3,4}){1,2}$/.test(hex);
			const getChunksFromString = (st, chunkSize) => st.match(new RegExp(`.{${chunkSize}}`, "g"));
			const convertHexUnitTo256 = (hexStr) => parseInt(hexStr.repeat(2 / hexStr.length), 16);

			const getAlphafloat = (a, alpha) => {
				if (typeof a !== "undefined") { return a / 255 }
				if ((typeof alpha != "number") || alpha < 0 || alpha > 1) {
					return 1
				}
				return alpha
			}

			const hexToRGBA = (hex, alpha) => {
				if (!isValidHex(hex)) { throw new Error("Invalid HEX") }
				const chunkSize = Math.floor((hex.length - 1) / 3)
				const hexArr = getChunksFromString(hex.slice(1), chunkSize)
				const [r, g, b, a] = hexArr.map(convertHexUnitTo256)
				return { r, g, b, a };
			}

			return hexToRGBA(hex);
		}

		// Check the format of the color, HEX or RGB?
		if (color.match(/^rgb/)) {

			// If HEX --> store the red, green, blue values in separate variables
			color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);
			r = color[1];
			g = color[2];
			b = color[3];
		} else {

			// If RGB --> Convert it to HEX: http://gist.github.com/983661
			color = hexToRgba(color);

			if (color.a < 80) return 'light'; // the alpha channel this light, makes any color light

			r = color.r; // >> 16;
			g = color.g; // >> 8 & 255;
			b = color.b; // & 255;
		}

		// HSP (Highly Sensitive P) equation from http://alienryderflex.com/hsp.html
		hsp = Math.sqrt(
			0.299 * (r * r) +
			0.587 * (g * g) +
			0.114 * (b * b)
		);

		// Using the HSP value, determine whether the color is light or dark
		if (hsp > 127.5) {

			return 'light';
		} else {

			return 'dark';
		}
	}
};